/**
 * Display a user's profile: name, biography, picture, and badges.
 *
 * Does not display email, as the page URL is guessable from userid.
 * @module components/Views/ProfileView
 */
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Container } from '@plone/components';
import SocialNetworks from '@plonegovbr/volto-social-media/components/SocialNetworks/SocialNetworks';
import { Helmet } from '@plone/volto/helpers/Helmet/Helmet';
import Badges from '../Badge/Badges';

import type { ProfileContent, ProfileImage } from '../../types';

import './ProfileView.scss';

const messages = defineMessages({
  noName: { id: 'profile-view-unnamed', defaultMessage: 'Unnamed user' },
});

interface ProfileViewProps {
  content: ProfileContent;
}

/**
 * Return the best available URL for a profile picture.
 *
 * Prefers a scaled version to avoid downloading large originals.
 *
 * @param image The serialized image field.
 * @returns The URL, or null if no picture exists.
 */
export function pictureUrl(
  image: ProfileImage | null | undefined,
): string | null {
  if (!image) {
    return null;
  }
  return (
    image.scales?.preview?.download ??
    image.scales?.mini?.download ??
    image.download ??
    null
  );
}

const ProfileView: React.FC<ProfileViewProps> = ({ content }) => {
  const intl = useIntl();
  // Same order as the backend's `Title()`: full name, login, then the id.
  // `content.title` is never serialized.
  const name =
    content.fullname ||
    content.login ||
    content.id ||
    intl.formatMessage(messages.noName);
  const picture = pictureUrl(content.image);

  return (
    <Container className="view-wrapper identity-profile-view ui">
      <Helmet title={name} />
      <div className="identity-profile-view__header">
        {picture ? (
          <img
            className="identity-profile-view__picture"
            src={picture}
            // Just the name: the heading already says whose page it is.
            alt={name}
          />
        ) : null}
        {/* The login beside the name, only when serialized and different
            from the heading. */}
        <h1 className="documentFirstHeading">
          {content.login && content.login !== name
            ? `${name} (${content.login})`
            : name}
        </h1>
      </div>
      {content.description ? (
        <p className="documentDescription">{content.description}</p>
      ) : null}
      {/* No guard: `Badges` renders nothing for an empty list. */}
      <Badges items={content.badges} />
      {/* Only with links: `SocialNetworks` throws on `undefined`. */}
      {content.social_links?.length ? (
        <Container className="socialNetworks">
          <SocialNetworks networks={content.social_links} />
        </Container>
      ) : null}
    </Container>
  );
};

export default ProfileView;
